import { settings, addTrailingSlash } from "@thegetty-private/rad-app-utils";

/**
 * Gets the Non-open image request form POST URL from the server environment
 * @return {String} -- the URL value
 */
export function getImageRequestClosedUrl() {
  return settings.getConfigValue(
    "IMAGE_REQUEST_NON_OPEN_URL",
    undefined,
    "https://www.getty.edu/license/license"
  );
}

/**
 * Gets the open image request form POST URL from the server environment
 * @return {String} -- the URL value
 */
export function getImageRequestOpenUrl() {
  return settings.getConfigValue(
    "IMAGE_REQUEST_OPEN_URL",
    undefined,
    "https://www.getty.edu/art/collection/download"
  );
}

/**
 * Gets the Data URL where to request data from (LOD Gateway or local dev)
 * @param {String} defaultValue - the default data url
 *
 * @return {String} -- the URL value
 */
export function getDataUrl(defaultValue) {
  return settings.getDataUrl(defaultValue);
}

/**
 * Returns IIIF gateway endpoint
 */
export function getIIIFGatewayURL(
  defaultValue = "https://media.getty.edu/iiif"
) {
  return settings.getIIIFGatewayURL(defaultValue);
}

/**
 * Gets the Media Gateway URL where to request data from (LOD Gateway or local dev)
 * @param {String} defaultValue - the default data url
 *
 * @return {String} -- the URL value
 */
export function getMediaGatewayUrl(defaultValue) {
  return settings.getMediaGatewayUrl(defaultValue);
}

export function getApiPath(defaultValue) {
  let url = settings.getConfigValue("API_PATH", undefined, defaultValue);
  url = addTrailingSlash(url);
  return url;
}

/**
 * Gets the GettyUI BaseURL
 * this is the URL from where the GettyUI ApplicationBaseUrl should point
 * @return {String} -- the URL value
 */
export function getGettyUIBaseUrl() {
  return settings.getConfigValue(
    "GETTY_UI_BASE_URL",
    undefined,
    "https://www.getty.edu"
  );
}

export function getFeedbackEmail() {
  return settings.getConfigValue(
    "FEEDBACK_EMAIL_ADDRESS",
    undefined,
    "MuseumCollections@getty.edu"
  );
}

/**
 * Gets the max number of fragment parts (links) to display in HMO metadata
 *
 * @return {integer}
 */
export function getMaxFragmentsToDisplay() {
  return settings.getConfigValue("MAX_FRAGMENTS_TO_DISPLAY", undefined, 10);
}

/**
 * Checks if the feature flag to batch id manager requests is on
 * @return {Boolean}
 */
export function idManagerBatchRequests() {
  return settings.getBooleanConfigValue(
    "ID_MANAGER_BATCH_REQUESTS",
    undefined,
    true
  );
}

/**
 * Checks feature flag to show similar search results
 * (should always be off in production)
 * @return {Boolean}
 */
export function showSimilarResults() {
  return settings.getBooleanConfigValue(
    "SHOW_SIMILAR_RESULTS",
    undefined,
    false
  );
}

/**
 * The domain name used by plausible.io
 *
 * @returns {String}
 */
export function getPlausibleDomain() {
  return settings.getConfigValue("PLAUSIBLE_DOMAIN");
}

/**
 * The Base URL used by the ID Manager Data URL
 *
 * @returns {String}
 */
export function getIdManagerDataUrl(
  defaultValue = getDataUrl("https://data.getty.edu/museum/collection/")
) {
  return addTrailingSlash(
    settings.getConfigValue("ID_MANAGER_DATA_URL", undefined, defaultValue)
  );
}

/**
 * Whether to use the simplified search logic that strips stop words
 *
 * @returns {boolean}
 */
export function useSimplifiedSearchQuery() {
  return (
    settings.getConfigValue("SIMPLIFIED_SEARCH_QUERY", undefined, undefined) !=
    undefined
  );
}

/**
 * Whether to disable the landscape orientation of the object profile
 *
 * @returns {boolean}
 */
export function isLandscapeViewDisabled() {
  return settings.getBooleanConfigValue(
    "DISABLE_LANDSCAPE_ORIENTATION",
    undefined,
    false
  );
}

/**
 * Gets the Mirador Viewer URL
 * @param {String} url - the iiif manifest url
 *
 * @returns {String} the mirador viewer url
 */
export function getMiradorUrl(url) {
  const miradorViewerBaseUrl = settings.getConfigValue(
    "MIRADOR_URL",
    undefined,
    "https://staging-tools.aws.getty.edu/iiifviewer/"
  );
  return miradorViewerBaseUrl + "?manifest=" + url;
}

/**
 * Gets the URL for id manager links for an indexed id
 * @param {String} indexedID - the indexedId
 *
 * @returns {String} the id manager url
 */
export function getIdManagerLinksUrl(indexedId) {
  let idManagerUrl = settings.getConfigValue(
    "ID_MANAGER_URL",
    undefined,
    "https://services.getty.edu/id-management/"
  );
  return `${idManagerUrl}links/?any_id=${indexedId}`;
}

/**
 * whether to include parts in our queries or not
 * @returns {boolean}
 */
export function includePartsInQueries() {
  return settings.getBooleanConfigValue(
    "INCLUDE_PARTS_IN_QUERIES",
    undefined,
    true
  );
}

/**
 * Whether to prioritize the loading of the object profile over
 * lower components on the HumanMadeObject page
 */
export function prioritizeObjectProfile() {
  return settings.getBooleanConfigValue(
    "PRIORITIZE_OBJECT_PROFILE",
    undefined,
    false
  );
}

/**
 * Whether to hide duplicate artist entries in HumanMadeObject pages and search result cards
 *
 * @returns {boolean} whether to hide (true) or display (false) duplicate artist entries
 */
export function hideArtistDupes() {
  return settings.getBooleanConfigValue(
    "HIDE_DUPLICATE_ARTISTS",
    undefined,
    true
  );
}

/**
 * Whether to hide manuscript child part images when duplicated
 *
 * @returns {boolean} whether to hide (true) or display (false) duplicate manuscript part images
 */
export function hideDupeManuscriptImages() {
  return settings.getBooleanConfigValue(
    "HIDE_DUPLICATE_MANUSCRIPT_IMAGES",
    undefined,
    false
  );
}

/**
 * Uses the description indexed value (which includes the prefix/suffix) instead of the 'primary_name'
 *
 * @returns {boolean}
 */
export function useArtistLongDescription() {
  return settings.getBooleanConfigValue(
    "DISPLAY_ARTIST_DESCRIPTION_INSTEAD_OF_NAME",
    undefined,
    true
  );
}

/**
 * Checks whether we are using custom manifest(s) with the Canvas Panel viewer
 *
 * @returns {boolean} - whether to use the custom IIIF manifest, if available
 * @default false
 */
export function useCustomIIIFManifest() {
  return settings.getBooleanConfigValue(
    "USE_CUSTOM_IIIF_MANIFEST",
    undefined,
    false
  );
}

/**
 * Checks whether we should only display custom manifest in viewer (aka Bayard)
 *
 * @returns {boolean} - whether to only display custom manifest in CP book viewer
 * @default true
 */
export function displayCustomManifestOnly() {
  return settings.getBooleanConfigValue(
    "ENABLE_DISPLAY_CUSTOM_ONLY",
    undefined,
    false
  );
}

export function useAlphabeticTypeaheadSort() {
  return settings.getBooleanConfigValue(
    "USE_ALPHABETIC_TYPEAHEAD_SORT",
    undefined,
    true
  );
}

/**
 * Checks whether we should display the Getty location for an active exhibition page
 *
 * @returns {boolean} - whether to display the Getty location on an active exhibition
 * @default false
 */
export function displayGettyExhibitionLocation() {
  return settings.getBooleanConfigValue(
    "DISPLAY_GETTY_EXHIBITION_LOCATION",
    undefined,
    false
  );
}

/**
 * Checks whether we should display the Getty location for an active exhibition page
 *
 * @returns {boolean} - whether to display the Getty location on an active exhibition
 * @default false
 */
export function displayUlanMetadata() {
  return settings.getBooleanConfigValue(
    "DISPLAY_ULAN_METADATA",
    undefined,
    false
  );
}
/**
 * Checks whether we should ignore the media host (in staging data) due to known discrepancies
 *
 * @returns {boolean} - whether to ignore media host
 * @default false
 */
export function ignoreMediaHost() {
  return settings.getBooleanConfigValue("IGNORE_MEDIA_HOST", undefined, false);
}

/**
 * gets an alert object from the environment if the right values are set
 *
 * @returns {object}
 */
export function getAlert() {
  const urgent = settings.getBooleanConfigValue(
    "ALERT_IS_URGENT",
    undefined,
    true
  );
  const title = settings.getConfigValue("ALERT_TYPE", undefined, "");
  const content = settings.getConfigValue("ALERT_MESSAGE", undefined, "");
  if (content != undefined && content.trim() != "") {
    return {
      active: true,
      content,
      title,
      uid: "5FHi8FDXBzBOzhdNAjuZMK",
      urgent
    };
  }
  return;
}

/**
 * Gets the preferred IIIF version to use for manifests
 * @return {Number} -- IIIF version {2 | 3}
 */
export function getIIIFVersion() {
  return parseInt(settings.getConfigValue("IIIF_VERSION", undefined, "2"));
}
